import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight, ShieldCheck } from 'lucide-react';
import dayjs from 'dayjs';
import ContactAddressForm from 'components/forms/employees/ContactAddressForm';
import CredentialsRoleForm from 'components/forms/employees/CredentialsRoleForm';
import PersonalDetailsForm from 'components/forms/employees/PersonalDetailsForm';
import localeTranslator from 'library/Hocs/LocalTranslator';
import { Employee, User } from 'models';
import services from 'services';
import './style.css';
var NewEmployee = function (props) {
    var t = props.translate;
    var navigate = useNavigate();
    var user = useState(function () { return new User(); })[0];
    var employee = useState(function () { return new Employee(); })[0];
    var _a = useState(false), disabled = _a[0], setDisabled = _a[1];
    var form = Form.useForm()[0];
    var employeeService = services.employeeService, jobTitleService = services.jobTitleService;
    var jobTitles = jobTitleService.useJobTitles().data;
    var _b = employeeService.useCreateEmployee(), createEmployee = _b.mutate, requestStatus = _b.status;
    var syncModelsFromForm = function () {
        var _a;
        var values = form.getFieldsValue(true);
        if (values.fullName)
            user.setName(String(values.fullName).trim());
        if (values.gender)
            user.setGender(String(values.gender));
        if (values.email)
            user.setEmail(String(values.email).trim());
        if (values.mobile)
            user.setMobile(String(values.mobile).trim());
        if (values.secondaryPhone != null && String(values.secondaryPhone).trim()) {
            user.setHomePhone(String(values.secondaryPhone).trim());
        }
        if (values.address)
            user.setAddress(String(values.address).trim());
        if (values.password)
            user.setPassword(String(values.password));
        if (values.passwordConfirmation) {
            user.setPasswordConfirmation(String(values.passwordConfirmation));
        }
        var birthdate = values.birthdate;
        if ((_a = birthdate === null || birthdate === void 0 ? void 0 : birthdate.isValid) === null || _a === void 0 ? void 0 : _a.call(birthdate)) {
            user.setBirthDay(birthdate.toDate());
        }
        if (values.nationalId) {
            employee.setNationalId(String(values.nationalId).trim());
        }
        if (values.maritalStatus) {
            employee.setMartialStatusId(String(values.maritalStatus));
        }
        if (values.jobTitle != null && jobTitles) {
            var jobTitle = jobTitles.find(function (item) { return item.getId() === Number(values.jobTitle); });
            if (jobTitle) {
                employee.setJobTitle(jobTitle);
                if (!employee.salary_amount && jobTitle.getBaseSalary()) {
                    employee.setSalary(jobTitle.getBaseSalary());
                }
            }
        }
        if (!employee.started_at) {
            employee.started_at = dayjs().format('YYYY-MM-DD');
        }
        if (employee.salary_amount == null || Number.isNaN(Number(employee.salary_amount))) {
            employee.setSalary(0);
        }
        employee.setUser(user);
    };
    var submit = function () {
        setDisabled(true);
        syncModelsFromForm();
        createEmployee({
            employee: employee,
            successMessage: t('employeeCreated')
        });
    };
    useEffect(function () {
        if (requestStatus === 'error') {
            setDisabled(false);
        }
    }, [requestStatus]);
    var scrollToFirstError = function () {
        form.scrollToField('fullName', { behavior: 'smooth' });
    };
    return (_jsxs("div", { className: "employee-form-page", children: [_jsxs("header", { className: "employee-form-page__header", children: [_jsxs("nav", { className: "employee-form-page__breadcrumb", "aria-label": "Breadcrumb", children: [_jsx("span", { className: "employee-form-page__crumb employee-form-page__crumb--muted", children: t('breadcrumbClinic') }), _jsx(ChevronRight, { size: 13, strokeWidth: 2, "aria-hidden": true }), _jsx(Link, { to: "/employees", className: "employee-form-page__crumb", children: t('breadcrumbEmployees') }), _jsx(ChevronRight, { size: 13, strokeWidth: 2, "aria-hidden": true }), _jsx("span", { className: "employee-form-page__crumb employee-form-page__crumb--current", children: t('breadcrumbNew') })] }), _jsxs("div", { className: "employee-form-page__title-row", children: [_jsx("button", { type: "button", className: "employee-form-page__back", onClick: function () { return navigate('/employees'); }, "aria-label": t('common:buttons.cancel'), children: _jsx(ArrowLeft, { size: 18, strokeWidth: 2 }) }), _jsx("h1", { className: "employee-form-page__title", children: t('title') })] })] }), _jsxs("div", { className: "employee-form-page__canvas", children: [_jsxs(Form, { className: "employee-form", layout: "vertical", form: form, onFinish: submit, onFinishFailed: scrollToFirstError, requiredMark: false, children: [_jsx(PersonalDetailsForm, { employee: employee, user: user }), _jsx(ContactAddressForm, { user: user }), jobTitles ? (_jsx(CredentialsRoleForm, { employee: employee, user: user, jobTitles: jobTitles })) : null, _jsxs("div", { className: "employee-form-page__footer", children: [_jsxs("p", { className: "employee-form-page__hint", children: [_jsx("span", { className: "employee-form-page__hint-dot", "aria-hidden": true, children: "\u2022" }), _jsxs("span", { children: [t('requiredHintBefore'), _jsx("span", { className: "employee-form__req", children: "*" }), t('requiredHintAfter')] })] }), _jsxs("div", { className: "employee-form-page__footer-actions", children: [_jsx("button", { type: "button", className: "employee-form-page__btn employee-form-page__btn--ghost", onClick: function () { return navigate('/employees'); }, children: t('common:buttons.cancel') }), _jsxs("button", { type: "submit", className: "employee-form-page__btn employee-form-page__btn--primary", disabled: disabled, children: [_jsx(Check, { size: 16, strokeWidth: 2 }), _jsx("span", { children: t('save') })] })] })] })] }), _jsxs("div", { className: "employee-form-page__meta", children: [_jsxs("div", { className: "employee-form-page__meta-left", children: [_jsx(ShieldCheck, { size: 14, strokeWidth: 2, "aria-hidden": true }), _jsx("span", { children: t('rbacNote') }), _jsx("span", { className: "employee-form-page__meta-sep", "aria-hidden": true, children: "\u2022" }), _jsx("span", { children: t('encryptionNote') })] }), _jsx("span", { className: "employee-form-page__meta-id", children: t('facilityId') })] })] })] }));
};
export default localeTranslator(NewEmployee, 'employeeForm');
